/**
 * Intro Request program hook - effect runner.
 * 
 * Elm-ish architecture: This is the ONLY place that combines:
 * - useState/useReducer for state management
 * - dispatch for message handling
 * - update() for pure state transitions
 * - runEffect() for side effects (outside the reducer)
 * 
 * The reducer is PURE - it only returns new state.
 * Effects are queued in state and executed via useEffect.
 * 
 * This hook bridges the pure Elm model to React's rendering.
 * 
 * TODO(ACT 7): Emit intro_request_started audit event on page load
 */
import { useReducer, useEffect, useCallback, useRef } from 'react';
import { initialModel, type IntroRequestModel } from './model';
import { type IntroRequestMsg } from './messages';
import { update, type IntroRequestEffect } from './update';
import { runEffect, type EffectContext } from './effects';

/**
 * Result of the program hook - exposes model and action callbacks.
 */
export interface UseIntroRequestProgramResult {
  /** Current model state */
  readonly model: IntroRequestModel;
  /** Start the intro request flow with a card ID */
  readonly start: (cardId: string | null) => void;
  /** User edited the message field */
  readonly editMessage: (message: string) => void;
  /** User toggled the acknowledgement checkbox */
  readonly toggleAcknowledgement: (checked: boolean) => void;
  /** User clicked submit button */
  readonly submit: () => void;
  /** User clicked retry from error state */
  readonly retry: () => void;
  /** User clicked back from error state */
  readonly back: () => void;
}

/**
 * Program state - includes model and pending effects.
 * Effects are queued in state and executed outside the reducer.
 */
type ProgramState = {
  readonly model: IntroRequestModel;
  readonly pendingEffects: readonly IntroRequestEffect[];
};

/**
 * Initial program state.
 */
const initialState: ProgramState = {
  model: initialModel,
  pendingEffects: [],
};

/**
 * Create the effect context for running effects.
 * Uses the ref to get the current dispatch function.
 */
function createEffectContext(dispatch: (msg: IntroRequestMsg) => void): EffectContext {
  return { dispatch };
}

/**
 * The main program hook - orchestrates state and effects.
 * 
 * The reducer is PURE - effects are NOT run inside the reducer.
 * Instead, effects are queued and executed via useEffect.
 * 
 * Usage:
 * ```tsx
 * function IntroRequestPage() {
 *   const { model, start, editMessage, toggleAcknowledgement, submit, retry, back } = useIntroRequestProgram();
 *   
 *   useEffect(() => { start(cardId); }, [start, cardId]);
 *   
 *   return (
 *     <IntroRequestView
 *       model={model}
 *       onStart={start}
 *       onMessageEdit={editMessage}
 *       onAcknowledgementToggle={toggleAcknowledgement}
 *       onSubmit={submit}
 *       onRetry={retry}
 *       onBack={back}
 *     />
 *   );
 * }
 * ```
 */
export function useIntroRequestProgram(): UseIntroRequestProgramResult {
  const dispatchRef = useRef<((msg: IntroRequestMsg) => void)>(() => {});
  
  const [state, baseDispatch] = useReducer(
    (currentState: ProgramState, msg: IntroRequestMsg): ProgramState => {
      // Pure reducer - only returns new state
      const result = update(currentState.model, msg);
      
      return {
        model: result.model,
        pendingEffects: result.effects,
      };
    },
    initialState
  );

  // Keep dispatch ref updated for effect execution
  dispatchRef.current = baseDispatch;

  // Execute pending effects outside the reducer via useEffect
  useEffect(() => {
    if (state.pendingEffects.length === 0) return;

    const context = createEffectContext(baseDispatch);
    
    for (const effect of state.pendingEffects) {
      void runEffect(effect, context);
    }
  }, [state.pendingEffects, baseDispatch]);

  // Create action callbacks that dispatch messages
  const start = useCallback((cardId: string | null) => {
    // TODO(ACT 7): Emit intro_request_started audit event
    baseDispatch({ type: 'Started', cardId });
  }, []);

  const editMessage = useCallback((message: string) => {
    baseDispatch({ type: 'MessageEdited', message });
  }, []);

  const toggleAcknowledgement = useCallback((checked: boolean) => {
    baseDispatch({ type: 'AcknowledgementToggled', checked });
  }, []);

  const submit = useCallback(() => {
    baseDispatch({ type: 'SubmitClicked' });
  }, []);

  const retry = useCallback(() => {
    baseDispatch({ type: 'RetryClicked' });
  }, []);

  const back = useCallback(() => {
    baseDispatch({ type: 'BackClicked' });
  }, []);

  return {
    model: state.model,
    start,
    editMessage,
    toggleAcknowledgement,
    submit,
    retry,
    back,
  };
}